import { useApiClientUser } from '~/@api/client/useApiClientUser'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { IOnboardingStep } from '~/@api/generated-api-types-user/Api'
import { useAuthenticated } from '~/@pages_auth/_auth/useAuthenticated'
import { useQueryClient } from '~/@mid_query/client/useQueryClient'

const userQuery = 'user'

function useUser() {
  const client = useApiClientUser()
  const queryClient = useQueryClient()
  const { hasAuthToken } = useAuthenticated()

  async function fetchUser() {
    return client.user
      .accountInfoControllerGetInfo({ secure: true })
      .then((res) => res.data.user)
  }

  const {
    data: user,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: [userQuery],
    queryFn: fetchUser,
    placeholderData: keepPreviousData,
    refetchOnMount: true,
    retry: (failureCount) => {
      return failureCount < 3
    },
    retryDelay: 1000,
    throwOnError: true,
    enabled: hasAuthToken,
    meta: { logoutOnFailedToFetch: true },
  })

  const reloadUser = async () => {
    await queryClient.invalidateQueries({ queryKey: [userQuery] })
  }

  const clearUser = () => {
    queryClient.removeQueries({ queryKey: [userQuery] })
  }

  const isUserApproved = user?.onboarding?.isUserApproved ?? false

  return {
    user,
    isLoading,
    isError,
    error,
    reloadUser,
    clearUser,
    isUserApproved,
    currentOnboardingStep: user?.onboarding.currentRequiredStep,
    isCurrentStepEqual(step: IOnboardingStep | null) {
      return user?.onboarding.currentRequiredStep === step
    },
    isCurrentStepIdentityVerification:
      user?.onboarding.currentRequiredStep ===
      IOnboardingStep.IdentityVerification,
    isCurrentStepIdentityConfirmation:
      user?.onboarding.currentRequiredStep ===
      IOnboardingStep.IdentityVerificationConfirmed,
    isCurrentStepAfterPhoneVerification:
      isUserApproved ||
      (user?.onboarding.currentRequiredStep !==
        IOnboardingStep.EmailRegistration &&
        user?.onboarding.currentRequiredStep !==
          IOnboardingStep.PhoneVerification),
  }
}

export { useUser }
